<template>
    <div>
        <div class="main_div_word_controls">
            <div class="main_div_word_controls_container">
                <div class="main_div_word_bottom_left">
                    <select v-model="selectLang" @change="debouncedChangeLang" class="main_div_word_bottom_left_select" aria-label="tiktok text to speech select lang">
                        <option v-for="item in filteredLangs" v-bind:key="item.value" v-bind:value="item.value">
                            {{ item.label }}
                        </option>
                    </select>
                </div>
                <div class="main_div_word_bottom_left">
                    <select v-model="selectModel"
                        @change="debouncedChangeModel"
                        @scroll="handleScroll"
                        class="main_div_word_bottom_left_select2"
                        aria-label="tiktok tts select model"
                        :size="1"
                        @mousedown="handleSelectMouseDown">
                        <option v-for="option in filteredModels"
                            v-bind:key="option.value"
                            v-bind:value="option.value">
                            {{ option.label }}
                        </option>
                    </select>
                </div>
                <button
                    class="preview-voice-button"
                    @click="playPreviewAudio"
                    :class="{
                        'loading': isPreviewLoading,
                        'playing': isPreviewPlaying
                    }"
                    :disabled="!selectModelWavplay || isPreviewLoading">
                    <span v-if="!isPreviewLoading">
                        {{ isPreviewPlaying ? $t('txt2voice.audio_pause') : $t('txt2voice.preview_voice') }}
                    </span>
                    <span v-else class="loading-spinner"></span>
                </button>
            </div>
        </div>

        <div class="preset-tags-container">
            <button
                v-for="index in presetTextCount"
                :key="index"
                class="preset-tag"
                :class="{ 'active': activePresetIndex === index }"
                @click="setPresetText(index)">
                {{ $t(`txt2voice.preset_texts.${index}.tag`) }}
            </button>
        </div>

        <div class="main_div">
            <div class="main_div_word">
                <div class="main_div_word_teatarea">
                    <textarea
                        v-model="voicetext"
                        ref="textarea"
                        type="text"
                        :placeholder="placeholderText"
                        @input="onTextInput"
                        @focus="onTextFocus"
                        :maxlength="maxLength"
                        @blur="onTextBlur"
                        @compositionstart="onComposition"
                        @compositionend="onComposition"
                        class="textarea-style"
                        :class="{ 'composing': isComposing }"
                        aria-label="tiktok text to speech textarea"
                    >
                    </textarea>
                    <div class="word-counter">{{ wordcnt }}/{{ maxLength }}</div>
                </div>
                <div class="main_div_word_bottom">
                    <div class="main_div_word_bottom_right">
                        <button
                            ref="genButton"
                            class="main_div_bottom_right_button"
                            :class="{ 'loading': isLoading }"
                            v-on:click="handleGenVoiceClick"
                            v-bind:disabled="disabled || isLoading">
                            <span v-if="!isLoading">{{ $t('txt2voice.main_genvoice') }}</span>
                            <span v-else class="loading-spinner"></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="audio-controls-container">
            <div class="audio-controls" :class="{
                'visible': showAudioControls && generatedAudio && !isLoading,
                'hidden': isLoading
            }">
                <button
                    class="control-button play-button"
                    @click="togglePlay"
                    :disabled="!canPlay"
                    :class="{ 'loading': isPlayLoading }"
                >
                    <span v-if="!isPlayLoading">{{ isPlaying ? $t('txt2voice.audio_pause') : $t('txt2voice.audio_play') }}</span>
                    <span v-else class="loading-spinner"></span>
                </button>
                <button
                    class="control-button download-button"
                    @click="handleDownload"
                    :disabled="!generatedAudio || isDownloading"
                    :class="{ 'downloading': isDownloading }"
                >
                    {{ isDownloading ? $t('txt2voice.audio_downloading') : $t('txt2voice.audio_download') }}
                </button>
            </div>
        </div>
        <audio ref="audioPlayer" style="display: none;"></audio>
        <div id="popupContainer">
            <PopupModal
                v-for="(data, id) in popupData"
                :key="id"
                :data="data"
                :pricing-href="pricing_href"
                v-if="popupStates[id]"
                @close="closePopup(id)"
            />
        </div>
        <PopupDownload ref="popupDownload" />
        <GoogleSignInModal
            :visible.sync="isSignInVisible"
            @credential-response="handleCredentialResponse"
        />
    </div>
    
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import debounce from 'lodash/debounce';
import { defineAsyncComponent } from 'vue';
import { playAudio } from '../utils/audioUtils';
import { generateSignature } from '../utils/signature';
import { reportError } from '../utils/errorReporter'
import { ActionType, trackAction } from '../utils/actionReporter';
import { getOrCreateTkUid, fetchActionCounts, getUserInfo } from '../utils/tkUser';
import { HOST_CONFIG, TTS_CONFIG, PRESET_CONFIG, POPUP_TYPES } from '@config/tkconfig';
import { handleGoogleAuth } from '../utils/auth'

const tkuid = getOrCreateTkUid();
const maxLength = TTS_CONFIG.maxLength
const apiHost = HOST_CONFIG.api_host
const cdnHost = HOST_CONFIG.cdn_host
const domain = HOST_CONFIG.domain

export default {
    name: 'VoiceGenerator',
    props: {
        firstPresetText: {
            type: String,
            default: ''
        },
        defaultModelcat: {
            type: String,
            default: ''
        },
        defaultModel: {
            type: String,
            default: ''
        }
    },
    components: {
        PopupModal: defineAsyncComponent({
            loader: () => import('../components/PopupModal.vue'),
            timeout: 2000
        }),
        PopupDownload: defineAsyncComponent({
            loader: () => import('../components/PopupDownload.vue'),
            timeout: 2000
        }),
        GoogleSignInModal: defineAsyncComponent({
            loader: () => import('../components/GoogleSignInModal.vue'),
            timeout: 2000
        })
    },
    data() {
        return {

            langsModels: {},
            allLangs: [],
            selectLang: null,
            selectLangModels: null,
            selectModel: null,
            selectModelWavplay: '',
            modelCategory: '',
            modelName: '',
            voicetext: '',
            disabled: false,
            wordcnt: 0,
            email: '',
            user_subscript: 0,
            pricing_href: '/en/pricing',
            isComposing: false,
            inputTimer: null,
            pageSize: 25,
            currentPage: 0,
            isLoadingMore: false,
            isLoading: false,
            isDownloading: false,
            isSignInVisible: false,
            popupData: {
                [POPUP_TYPES.NOLOGIN]: {
                    title: this.$i18n.t('txt2voice.popup_nologin_title'),
                    description: this.$i18n.t('txt2voice.popup_nologin_desc'),
                    buttonText: this.$i18n.t('txt2voice.popup_nologin_btntext'),
                },
                [POPUP_TYPES.NOSUB]: {
                    title: this.$i18n.t('txt2voice.popup_nosub_title'),
                    description: this.$i18n.t('txt2voice.popup_nosub_desc'),
                    buttonText: this.$i18n.t('txt2voice.popup_nosub_btntext'),
                },
                [POPUP_TYPES.SUBLIMIT]: {
                    title: this.$i18n.t('txt2voice.popup_sublimit_title'),
                    description: this.$i18n.t('txt2voice.popup_sublimit_desc'),
                    buttonText: this.$i18n.t('txt2voice.popup_sublimit_btntext'),
                }
            },
            popupStates: {},
            isPreviewLoading: false,
            isPlaying: false,
            showAudioControls: false,
            isPlayLoading: false,
            isPreviewPlaying: false,
            activePresetIndex: 1,
            generatedAudio: '',
            maxLength: maxLength
        }
    },
    computed: {
        ...mapGetters(['isLoggedIn', 'currentUser', 'authToken']),
        placeholderText() {
            return this.$i18n.locale === 'en'
                ? ''
                : this.$t('txt2voice.main_textarea_holder');
        },
        filteredLangs() {
            return this.allLangs?.slice(0, 25);
        },
        filteredModels() {
            if (!this.selectLangModels) return [];
            return this.selectLangModels.slice(0, 120);
        },
        canPlay() {
            return !!(this.generatedAudio && !this.isLoading);
        },
        isDisabled() {
            return !this.voicetext?.trim();
        },
        presetTextCount() {
            let count = 0;
            let index = 1;
            while (this.$te(`txt2voice.preset_texts.${index}`)) {
                count++;
                index++;
            }
            return count;
        }
    },
    watch: {
        isLoggedIn(newValue) {
            if (newValue && this.currentUser) {
                this.getUserInfo(this.currentUser.email);
            }
        },
        '$i18n.locale'(newVal) {
            if (newVal !== 'en') {
                this.activePresetIndex = null;
            }
        },
        firstPresetText: {
            immediate: true,
            handler(newText) {
                if (newText) {
                    this.voicetext = newText;
                    this.wordcnt = this.voicetext.length;
                    this.activePresetIndex = 1;
                }
            }
        },
        defaultModelcat: {
            handler(newValue) {
                if (newValue && newValue in this.langsModels) {
                    this.selectLang = newValue;
                    this.selectLangModels = this.langsModels[newValue].slice();
                }
            }
        },
        defaultModel: {
            handler(newValue) {
                if (newValue && this.selectLangModels) {
                    const model = this.selectLangModels.find(m => m.value === newValue);
                    if (model) {
                        this.selectModel = newValue;
                        this.selectModelWavplay = model.wavplay || '';
                    }
                }
            }
        }
    },
    methods: {
        initializePopups() {
            Object.keys(this.popupData).forEach(id => {
                this.popupStates[id] = false
            })
        },
        openPopup(id) {
            if (this.popupData[id]) {
                this.popupStates[id] = true
            }
        },
        closePopup(id) {
            if (this.popupStates[id] !== undefined) {
                this.popupStates[id] = false
            }
            this.$forceUpdate();
        },
        async getUserInfo(email) {
            const result = await getUserInfo(email);
            if (result.ret === 0) {
                this.email = result.email;
                this.user_subscript = result.userSubscript;
            }
        },
        onLangChange(evt) {
            const value = evt?.target?.value;
            if (!value || !(value in this.langsModels)) return;

            this.selectLang = value;
            this.selectLangModels = this.langsModels[value].slice();
            
            // 设置默认模型
            if (this.selectLangModels.length > 0) {
                const defaultModel = this.selectLangModels[0];
                this.selectModel = defaultModel.value;
                this.selectModelWavplay = defaultModel.wavplay || '';
            } else {
                this.selectModel = null;
                this.selectModelWavplay = '';
            }

            // 重置音频状态
            this.resetAudioState();
        },
        debouncedChangeLang: debounce(function(evt) {
            this.onLangChange(evt);
        }, 50),
        debouncedChangeModel: debounce(function(evt) {
            requestAnimationFrame(() => {
                this.changeModel(evt);
            });
        }, 50),
        changeModel(evt) {
            const value = evt?.target?.value;
            if (!value || !this.selectLangModels) return;

            const model = this.selectLangModels.find(m => m.value === value);
            if (!model) return;

            requestAnimationFrame(() => {
                this.selectModel = value;
                this.selectModelWavplay = model.wavplay || '';
                this.resetAudioState();
            });
        },
        async playPreviewAudio() {
            if (this.isPreviewLoading || !this.selectModelWavplay) return;

            const audio = this.$refs.audioPlayer;
            if (this.isPreviewPlaying) {
                audio.pause();
                this.isPreviewPlaying = false;
                return;
            }

            this.isPreviewLoading = true;
            try {
                await playAudio(audio, this.selectModelWavplay, cdnHost);
                this.isPreviewPlaying = true;
                audio.onended = () => {
                    this.isPreviewPlaying = false;
                    audio.onended = null;
                };
                // trackAction({
                //     email: this.email,
                //     action: ActionType.HOME_PREVIEW_AUDIO,
                //     domain: 'tiktokvoice.net',
                //     modelcat: this.modelcat,
                //     modelname: this.selectModel,
                //     tkuid: tkuid
                // });
            } catch (error) {
                reportError(error, 'playPreviewAudio');
                this.$emit('error', error);
            } finally {
                this.isPreviewLoading = false;
            }
        },
        async handleGenVoiceClick() {
            if (this.isLoading) return;
            requestAnimationFrame(() => {
                this.isLoading = true;
            });
            try {
                await this.generateVoice();
            } finally {
                requestAnimationFrame(() => {
                    this.isLoading = false;
                });
            }
        },
        async generateVoice() {
            const trimmedText = this.voicetext?.trim();
            if (!trimmedText) {
                alert(this.$i18n.t('txt2voice.main_input_empty'));
                return;
            }
            this.showAudioControls = false;

            if (!this.isLoggedIn) {
                const { dayCount, monthCount } = await fetchActionCounts({
                    action: ActionType.HOME_GEN_AUDIO,
                    uid: tkuid,
                    email: this.email,
                })
                if (dayCount >= 5 || monthCount >= 10) {
                    this.isSignInVisible = true;
                    trackAction({
                        email: this.email,
                        action: ActionType.HOME_GENPOPUP_LOGIN,
                        domain: 'tiktokvoice.net',
                        modelcat: this.modelcat,
                        modelname: this.modelname,
                        tkuid: tkuid
                    });
                    return;
                }
            } else {
                if ( this.user_subscript !== 1 ) {
                    const { dayCount, monthCount } = await fetchActionCounts({
                        action: ActionType.HOME_GEN_AUDIO,
                        uid: tkuid,
                        email: this.email,
                    })
                    if (dayCount >= 6 || monthCount >= 15) {
                        this.openPopup(POPUP_TYPES.NOSUB);
                        trackAction({
                            email: this.email,
                            action: ActionType.HOME_GENPOPUP_SUBSCRIPT,
                            domain: 'tiktokvoice.net',
                            modelcat: this.modelcat,
                            modelname: this.modelname,
                            tkuid: tkuid
                        });
                        return;
                    }
                }
            }

            try {
                const [category, name] = this.selectModel.split('+');
                this.modelCategory = category;
                this.modelName = name;
                // 使用 UTC 时间戳（精确到秒）
                const tstamp = Math.floor(Date.now() / 1000);
                const snature = generateSignature(trimmedText, tstamp);

                const formData = {
                    modelcat: category,
                    modelname: name,
                    text: trimmedText,
                    email: this.email,
                    userid: tkuid,
                    domain: this.domain,
                    t: 1,
                    domain: domain,
                    tstamp: tstamp,
                    snature: snature
                };

                const uri = `${apiHost}/api/genaudio`
                const { data } = await axios.post(uri, formData, {
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8',
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    },

                })
                switch (data.ret) {
                    case 0:
                        await Promise.all([
                            this.updateAudioElements(`${data.uri}`),
                            this.updateCharacterCounter(data.textlen)
                        ])
                        this.showAudioControls = true
                        trackAction({
                            email: this.email,
                            action: ActionType.HOME_GEN_AUDIO,
                            domain: domain,
                            modelcat: this.modelcat,
                            modelname: this.modelname,
                            tkuid: tkuid
                        });
                        break
                    case 2:
                        const popid = this.isLoggedIn
                            ? (this.user_subscript === 2 ? POPUP_TYPES.SUBLIMIT : POPUP_TYPES.NOSUB)
                            : POPUP_TYPES.NOLOGIN
                        this.openPopup(popid)
                        trackAction({
                            email: this.email,
                            action: ActionType.HOME_GENPOPUP_SUBSCRIPT,
                            domain: domain,
                            modelcat: this.modelcat,
                            modelname: this.modelname,
                            tkuid: tkuid
                        });
                        break
                    default:
                        trackAction({
                            email: this.email,
                            action: ActionType.HOME_GEN_FAILED,
                            domain: domain,
                            modelcat: this.modelcat,
                            modelname: this.modelname,
                            tkuid: tkuid
                        });
                        alert(data.msg)
                }
            } catch (error) {
                reportError(error, 'genVoice generator error, tkuid:' + tkuid + ", email:" + this.email)
                alert('An error occurred while generating the voice, please try again later!')
            }
        },
        async updateAudioElements(audioUrl) {
            try {
                this.generatedAudio = audioUrl;
                this.showAudioControls = true;
            } catch (error) {
                reportError(error, 'updateAudioElements');
            }
        },
        async updateCharacterCounter(textlen) {
            if (!this.email) return
            try {
                const uri = `${apiHost}/lapi/counter`
                const formData = new FormData()
                formData.append('email', this.email)
                formData.append('textlen', textlen)
                await axios.post(uri, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                })
            } catch (error) {
                reportError(error, 'updateCharacterCounter')
            }
        },
        togglePlay() {
            const audio = this.$refs.audioPlayer;
            if (!audio || !this.generatedAudio) {
                eportError(new Error('Audio element or source not found'), 'togglePlay');
                return;
            }

            if (this.isPlaying) {
                audio.pause();
                this.isPlaying = false;
                return;
            }

            this.isPlayLoading = true;
            playAudio(audio, this.generatedAudio)
                .then(() => {
                    this.isPlaying = true;
                    audio.onended = () => {
                        this.isPlaying = false;
                        audio.onended = null;
                    };
                    trackAction({
                        email: this.email,
                        action: ActionType.HOME_PLAY_AUDIO,
                        domain: 'tiktokvoice.net',
                        modelcat: this.modelcat,
                        modelname: this.modelname,
                        tkuid: tkuid
                    });
                })
                .catch(error => {
                    reportError(error, 'togglePlay - playback failed');
                    this.isPlaying = false;
                })
                .finally(() => {
                    this.isPlayLoading = false;
                });
        },
        async handleDownload() {
            if (!this.generatedAudio) {
                alert('There is no audio! Please generate audio first!');
                reportError(new Error('handleDownload - no audio'), 'handleDownload')
                return;
            }
            if (this.isDownloading) {
                reportError(new Error('handleDownload - downloading'), 'handleDownload')
                return;
            }
            if (!this.isLoggedIn) {
                this.isSignInVisible = true;
                trackAction({
                        email: this.email,
                        action: ActionType.HOME_DOWNPOPUP_LOGIN,
                        domain: 'tiktokvoice.net',
                        modelcat: this.modelcat,
                        modelname: this.modelname,
                        tkuid: tkuid
                    });
                return;
            }
            if (this.user_subscript !== 1) {
                try {
                    const { dayCount, monthCount } = await fetchActionCounts({
                        action: ActionType.HOME_DOWNLOAD_SUCCESS,
                        uid: tkuid,
                        email: this.email,
                    })

                    if (dayCount >= 1 || monthCount >= 2) {
                        // 当日下载超过1次，月下载超过5次，显示弹窗
                        this.$refs.popupDownload.openPopup();
                        trackAction({
                            email: this.email,
                            action: ActionType.HOME_DOWNPOPUP_SUBSCRIPT,
                            domain: 'tiktokvoice.net',
                            modelcat: this.modelcat,
                            modelname: this.modelname,
                            tkuid: tkuid
                        });
                        return ;
                    }
                } catch (error) {
                    reportError(error, 'handleDownload user subscript!')
                }
            }
            this.isDownloading = true;
            let url;
            let response;
            try {
                response = await fetch(`${apiHost}${this.generatedAudio}`, {
                    headers: {
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    },
                    mode: 'cors',
                    credentials: 'same-origin'
                });
                if (!response.ok) {
                    reportError(new Error(`Download failed with status: ${response.status}`), 'handleDownload');
                    alert('Audio download failed, Please try later!');
                    return;
                }
                await trackAction({
                    email: this.email,
                    action: ActionType.HOME_DOWNLOAD_SUCCESS,
                    domain: 'tiktokvoice.net',
                    modelcat: this.modelcat,
                    modelname: this.modelname,
                    tkuid: tkuid
                });
                const blob = await response.blob();
                if (!blob || blob.size === 0) {
                    reportError(new Error('Invalid blob data received'), 'handleDownload');
                    alert('Audio blob download failed, Please try later!');
                    return ;
                }
                url = window.URL.createObjectURL(blob);
                const filename = `tiktokvoice.net-${new Date().getTime()}.mp3`;
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } catch (error) {
                reportError(error, 'handleDownload down error');
                alert('Failed to download audio file, please try later!');
            } finally {
                if (url) {
                    window.URL.revokeObjectURL(url);
                }
                this.isDownloading = false;
            }
        },
        onTextInput: debounce(function(event) {
            if (this.isComposing) return;
            Promise.resolve().then(() => {
                requestAnimationFrame(() => {
                    const value = event.target.value
                    this.$nextTick(() => {
                        this.voicetext = value
                        this.updateWordCount();
                    })
                })
            })
        }, 16),
        updateWordCount() {
            const text = this.voicetext?.trim() || '';
            if (text.length > this.maxLength) {
                this.voicetext = text.slice(0, this.maxLength);
            }
            this.wordcnt = this.voicetext.length;
        },
        onComposition(event) {
            requestAnimationFrame(() => {
                this.isComposing = event.type === 'compositionstart'
                if (event.type === 'compositionend') {
                    queueMicrotask(() => {
                        this.onTextInput(event);
                    })
                }
            })
        },
        onTextFocus(event) {
            requestAnimationFrame(() => {
                const target = event.target;
                const placeholder = target.getAttribute('placeholder');
                if (target.value === placeholder) {
                    target.value = '';
                    this.voicetext = '';
                }
            });
        },
        onTextBlur(event) {
            requestAnimationFrame(() => {
                const target = event.target;
                if (!target.value.trim()) {
                    const placeholder = target.getAttribute('placeholder');
                    target.value = placeholder;
                }
            });
        },
        handleScroll: debounce(function(event) {
            const select = event.target;
            if (select.scrollTop + select.clientHeight >= select.scrollHeight - 50) {
                this.loadMoreOptions();
            }
        }, 50),
        handleSelectMouseDown(event) {
            // 使用 requestAnimationFrame 优化滚动检测
            if (!this.isLoadingMore) {
                requestAnimationFrame(() => {
                    const target = event.target;
                    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 50) {
                        this.loadMoreOptions();
                    }
                });
            }
        },
        async loadMoreOptions() {
            if (this.isLoadingMore) return;
            this.isLoadingMore = true;
            try {
                await new Promise(resolve => requestAnimationFrame(resolve));
                this.currentPage++;
            } finally {
                this.isLoadingMore = false;
            }
        },
        setPresetText(index) {
            this.activePresetIndex = index;
            this.voicetext = this.$t(`txt2voice.preset_texts.${index}.text`);
            this.updateWordCount();
        },
        resetAudioState() {
            const audio = this.$refs.audioPlayer;
            if (audio && !audio.paused) {
                audio.pause();
            }
            this.isPlaying = false;
            this.isPreviewPlaying = false;
            this.showAudioControls = false;
            this.generatedAudio = '';
        },
        async handleCredentialResponse(response) {
            try {
                const apiUrl = `${apiHost}/lapi/auth/google`
                const success = await handleGoogleAuth?.handleCredentialResponse?.(response, this.$store, apiUrl)
                if (success) {
                    this.isSignInVisible = false
                    trackAction({
                        email: this.email,
                        action: ActionType.HOME_LOGIN_SUCCESS,
                        domain: 'tiktokvoice.net',
                        modelcat: this.modelcat,
                        modelname: this.modelname,
                        tkuid: tkuid
                    });
                } else {
                    trackAction({
                        email: this.email,
                        action: ActionType.HOME_LOGIN_FAILED,
                        domain: 'tiktokvoice.net',
                        modelcat: this.modelcat,
                        modelname: this.modelname,
                        tkuid: tkuid
                    });
                    reportError(new Error('Authentication failed'), 'handleCredentialResponse failed')
                }
            } catch (error) {
                reportError(error, 'handleCredentialResponse')
            }
        },
        setupEventListeners() {
            // 错误处理监听
            this.errorHandler = (event) => reportError(event.error, 'window.error')
            this.rejectionHandler = (event) => reportError(event.reason, 'unhandledrejection')
            window.addEventListener('error', this.errorHandler)
            window.addEventListener('unhandledrejection', this.rejectionHandler)

            // 初始化下载弹窗引用
            this.$refs.popupDownload = this.$refs.popupDownload || {}

            // 可选: 初始化性能监控
            // try {
            //    onLCP((metric) => reportWebVital(metric))
            // } catch (error) {
            //    reportError(error, 'Failed to initialize INP monitoring')
            // }
        },
    },
    async created() {
        if (this.defaultModelcat && this.defaultModelcat in this.langsModels) {
            this.selectLang = this.defaultModelcat;
            this.selectLangModels = this.langsModels[this.defaultModelcat].slice();
        } else {
            // 否则使用 store 中的值
            this.selectLang = this.$store.state.selectLang;
            this.selectLangModels = this.$store.state.selectLangModels;
        }

        // 如果 props 中有默认模型，优先使用
        if (this.defaultModel && this.selectLangModels) {
            const model = this.selectLangModels.find(m => m.value === this.defaultModel);
            if (model) {
                this.selectModel = this.defaultModel;
                this.selectModelWavplay = model.wavplay || '';
            } else {
                // 否则使用 store 中的值
                this.selectModel = this.$store.state.selectModel;
                this.selectModelWavplay = this.$store.state.selectModelWavplay;
            }
        } else {
            // 否则使用 store 中的值
            this.selectModel = this.$store.state.selectModel;
            this.selectModelWavplay = this.$store.state.selectModelWavplay;
        }
        
        if (this.isLoggedIn && this.currentUser) {
            this.getUserInfo(this.currentUser.email);
        }
        this.$nextTick(() => {
            this.langsModels = this.$store.state.langsModels || {}
            this.allLangs = this.$store.state.allLangs || {}
            this.pricing_href = '/' + this.$store.state.lang + '/pricing'
        })
    },
    mounted () {
        // 文本框焦点
        const textarea = document.getElementById('voicetextarea')
        if (textarea) {
            textarea.focus()
        }
        // 使用 requestIdleCallback 在浏览器空闲时初始化非关键功能
        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
                this.initializePopups()
                this.setupEventListeners()
            }, { timeout: 2000 }) // 设置2秒超时,确保最终会执行
        } else {
            // 降级处理: 如果浏览器不支持 requestIdleCallback
            setTimeout(() => {
                this.initializePopups()
                this.setupEventListeners()
            }, 0)
        }
    },
    beforeUnmount() {
        const audio = this.$refs.audioPlayer;
        if (audio) {
            audio.pause();
            audio.src = '';
            audio.onended = null;
        }
    }
}
</script>

<style scoped>
</style> 